/**
 * Note AI Edit Selectors
 *
 * Memoized selectors for accessing note AI edit state with fallback
 */

import { createSelector } from '@reduxjs/toolkit';
import type { FeatureSelectorWithFallback } from '@store/storeTypes';
import type { RootState } from '@store/types';

import type { NoteAiEditState } from './chatSlice';

// ============================================================================
// Empty State Fallback
// ============================================================================

const emptyState: NoteAiEditState = {
  noteId: null,
  threadId: null,
  messages: [],
  activities: {},
  assistant: {
    status: 'idle',
    currentStep: null,
    streamingContent: '',
    currentMessageId: null,
    firstTokenReceived: false,
  },
  sectionUpdates: {},
  streamingSections: [],
  selectedSectionIdsForCopy: [],
  sectionPreviousContent: {},
  affectedSectionIds: [],
  dirtySections: {},
  interrupt: null,
  isChatVisible: true,
  isChatOnboardingVisible: false,
  focusedSection: null,
  error: null,
  ssePerformanceMetrics: null,
};

// ============================================================================
// Base Selector with Fallback
// ============================================================================

/**
 * Selects the note AI edit feature state with fallback
 */
export const selectNoteAiEditState: FeatureSelectorWithFallback<
  NoteAiEditState,
  typeof emptyState
> = (state: RootState) => state['noteAiEdit/ui'] || emptyState;

// ============================================================================
// Derived Selectors
// ============================================================================

/**
 * Selects the current note ID
 */
export const selectNoteId = createSelector(
  [selectNoteAiEditState],
  (state) => state.noteId
);

/**
 * Selects the current thread ID
 */
export const selectThreadId = createSelector(
  [selectNoteAiEditState],
  (state) => state.threadId
);

/**
 * Selects all chat messages
 */
export const selectMessages = createSelector(
  [selectNoteAiEditState],
  (state) => state.messages
);

/**
 * Selects the assistant status
 */
export const selectAssistantStatus = createSelector(
  [selectNoteAiEditState],
  (state) => state.assistant.status
);

/**
 * Selects the current step name
 */
export const selectCurrentStep = createSelector(
  [selectNoteAiEditState],
  (state) => state.assistant.currentStep
);

/**
 * Selects the streaming content
 */
export const selectStreamingContent = createSelector(
  [selectNoteAiEditState],
  (state) => state.assistant.streamingContent
);

/**
 * Selects whether the assistant is currently streaming
 */
export const selectIsStreaming = createSelector(
  [selectNoteAiEditState],
  (state) => state.assistant.status === 'streaming'
);

/**
 * Selects whether first token has been received
 */
export const selectFirstTokenReceived = createSelector(
  [selectNoteAiEditState],
  (state) => state.assistant.firstTokenReceived
);

/**
 * Selects the unified interrupt state
 */
export const selectInterrupt = createSelector(
  [selectNoteAiEditState],
  (state) => state.interrupt
);

/**
 * Selects the interrupt event type
 */
export const selectInterruptType = createSelector(
  [selectInterrupt],
  (interrupt) => interrupt?.eventType
);

/**
 * Checks if the current interrupt is a CONFIRMATION interrupt
 */
export const selectIsConfirmationInterrupt = createSelector(
  [selectInterrupt],
  (interrupt) => interrupt?.eventType === 'CONFIRMATION'
);

/**
 * Checks if the current interrupt is a MULTIPLE_CHOICE interrupt
 */
export const selectIsMultipleChoiceInterrupt = createSelector(
  [selectInterrupt],
  (interrupt) => interrupt?.eventType === 'MULTIPLE_CHOICE'
);

/**
 * Selects the current error
 */
export const selectError = createSelector(
  [selectNoteAiEditState],
  (state) => state.error
);

/**
 * Selects the most recent assistant message (for streaming indicator)
 */
export const selectMostRecentAssistantMessage = createSelector(
  [selectMessages],
  (messages) => {
    const assistantMessages = messages.filter((m) => m.role === 'assistant');
    return assistantMessages[assistantMessages.length - 1] || null;
  }
);

/**
 * Checks if a specific message is currently streaming
 */
export const makeSelectIsMessageStreaming = () =>
  createSelector(
    [selectMostRecentAssistantMessage, selectIsStreaming, (_state, messageId: string) => messageId],
    (mostRecentMessage, isStreaming, messageId) => {
      return isStreaming && mostRecentMessage?.id === messageId;
    }
  );

/**
 * Selects the IDs of sections affected by pending AI edits (independent of interrupt)
 * Now reads directly from state.affectedSectionIds instead of interrupt.edits
 */
export const selectAffectedSectionIds = createSelector(
  [selectNoteAiEditState],
  (state) => state.affectedSectionIds
);

/**
 * Selects the previous content snapshots (section_id -> content before AI edit)
 */
export const selectSectionPreviousContent = createSelector(
  [selectNoteAiEditState],
  (state) => state.sectionPreviousContent
);

/**
 * Selects section updates map (section_id -> pending content)
 */
export const selectSectionUpdates = createSelector(
  [selectNoteAiEditState],
  (state) => state.sectionUpdates
);

/**
 * Selects sections that are currently being streamed
 */
export const selectStreamingSections = createSelector(
  [selectNoteAiEditState],
  (state) => state.streamingSections
);

/**
 * Selects the current interrupt ID
 * Used for AG-UI resume requests
 */
export const selectCurrentInterruptId = createSelector(
  [selectInterrupt],
  (interrupt) => interrupt?.interruptId || null
);

/**
 * Selects the section IDs selected for copy functionality
 */
export const selectSelectedSectionIdsForCopy = createSelector(
  [selectNoteAiEditState],
  (state) => state.selectedSectionIdsForCopy
);

/**
 * Selects all activities (keyed by messageId)
 */
export const selectActivities = createSelector(
  [selectNoteAiEditState],
  (state) => state.activities
);

/**
 * Selects activities as an array sorted by timestamp (oldest first)
 */
export const selectActivitiesArray = createSelector([selectActivities], (activities) =>
  Object.values(activities).sort((a, b) => a.timestamp - b.timestamp)
);

/**
 * Selects whether the chat sidebar is visible
 */
export const selectIsChatVisible = createSelector(
  [selectNoteAiEditState],
  (state) => state.isChatVisible
);

/**
 * Selects whether the chat onboarding overlay is visible
 */
export const selectIsChatOnboardingVisible = createSelector(
  [selectNoteAiEditState],
  (state) => state.isChatOnboardingVisible
);

/**
 * Selects the currently focused section (for editing banner)
 */
export const selectFocusedSection = createSelector(
  [selectNoteAiEditState],
  (state) => state.focusedSection
);

/**
 * Selects SSE performance metrics for tracking and alerting
 */
export const selectSSEPerformanceMetrics = createSelector(
  [selectNoteAiEditState],
  (state) => state.ssePerformanceMetrics
);

/**
 * Selects dirty sections map (section_id -> current markdown content)
 * Used for copy functionality to get latest user edits
 */
export const selectDirtySections = createSelector(
  [selectNoteAiEditState],
  (state) => state.dirtySections ?? {}
);
